/**
 * Formateo para pantallas contables.
 *
 * El formato numerico NO es una preferencia del usuario: es del pais de la
 * entidad. Peru escribe S/ 1,234,567.89 y Chile $ 1.234.567,89 para el mismo
 * numero. Mostrar el separador equivocado en un libro es un error de datos.
 *
 * Por eso hay un locale activo a nivel de modulo que fija `I18nProvider` cuando
 * cambia la entidad, y todas las funciones lo respetan sin que cada pantalla
 * tenga que pasarlo. `formatAmount(x)` sigue funcionando igual que antes.
 */

export interface LocaleSpec {
  /**
   * BCP-47 de la SOCIEDAD: es-PE, es-CL, pt-BR, es-ES, en-US...
   *
   * Gobierna los NÚMEROS y las FECHAS NUMÉRICAS, y tiene que seguir al país
   * pase lo que pase: una factura brasileña se escribe `04/07/2026` y sus
   * importes `1.234.567,89`. Ver `localeTexto` para lo que sí sigue a la persona.
   */
  locale: string
  /**
   * El idioma de la PERSONA, sólo para texto con nombre propio: «julio», «jul».
   *
   * ===========================================================================
   * POR QUÉ SON DOS Y NO UNO
   * ===========================================================================
   *
   * Cesar: «cuando estoy en la sociedad de BR los períodos están en portugués,
   * ¿eso está bien?». No lo estaba. `LOCALES` mapea `BR -> pt-BR` y
   * `formatPeriod` usaba ese mismo locale, así que alguien con la interfaz en
   * español leía «julho de 2026». El idioma de la interfaz sigue a la PERSONA;
   * lo que sigue a la sociedad es la terminología fiscal —el ICMS es ICMS lo
   * mire quien lo mire— y el nombre de un mes no es terminología fiscal.
   *
   * **Y NO SE ARREGLA CAMBIANDO `locale` ENTERO**, que fue lo primero que
   * probé. Medido con `Intl`:
   *
   *     pt-BR   4 de julio -> 04/07/2026   1.234.567,89
   *     es-BR   4 de julio -> 04/07/2026   1,234,567.89   <- número roto
   *     en-BR   4 de julio -> 07/04/2026   1,234,567.89   <- DÍA Y MES INVERTIDOS
   *
   * Con la interfaz en inglés, una factura brasileña del 4 de julio se leería
   * «7 de abril». Un defecto que no da ningún error y cambia la fecha de un
   * documento fiscal.
   *
   * Por eso es una etiqueta de IDIOMA A SECAS —`es`, `pt`, `en`— y se usa sólo
   * donde el mes sale escrito con letras, que es donde no hay orden de día y
   * mes que romper.
   */
  localeTexto?: string
  currency: string
}

const DEFAULT_SPEC: LocaleSpec = { locale: "es-PE", localeTexto: "es", currency: "PEN" }

let activeSpec: LocaleSpec = DEFAULT_SPEC

/** Lo llama el I18nProvider al resolver el pais de la entidad activa. */
export function setActiveLocale(spec: LocaleSpec) {
  activeSpec = spec
}

export function getActiveLocale(): LocaleSpec {
  return activeSpec
}

/** Intl es caro de instanciar: una tabla de 800 filas lo llama 4.000 veces. */
const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>()

function numberFormat(locale: string, options: Intl.NumberFormatOptions) {
  const key = `n|${locale}|${JSON.stringify(options)}`
  const hit = cache.get(key)
  if (hit) return hit as Intl.NumberFormat
  const made = new Intl.NumberFormat(locale, options)
  cache.set(key, made)
  return made
}

function dateTimeFormat(locale: string, options: Intl.DateTimeFormatOptions) {
  const key = `d|${locale}|${JSON.stringify(options)}`
  const hit = cache.get(key)
  if (hit) return hit as Intl.DateTimeFormat
  const made = new Intl.DateTimeFormat(locale, options)
  cache.set(key, made)
  return made
}

function isBlank(value: number | null | undefined): value is null | undefined {
  return value === null || value === undefined || Number.isNaN(value)
}

/** Importe sin simbolo: lo normal dentro de una columna de libro. */
export function formatAmount(
  value: number | null | undefined,
  options?: { blankZero?: boolean; decimals?: number; spec?: LocaleSpec },
) {
  if (isBlank(value)) return ""
  if (options?.blankZero && value === 0) return ""
  const spec = options?.spec ?? activeSpec
  const decimals = options?.decimals ?? 2
  return numberFormat(spec.locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

/** Importe con simbolo y su posicion correcta (S/ antes, € despues). */
export function formatMoney(
  value: number | null | undefined,
  currency?: string,
  options?: { spec?: LocaleSpec },
) {
  if (isBlank(value)) return ""
  const spec = options?.spec ?? activeSpec
  return numberFormat(spec.locale, {
    style: "currency",
    currency: currency ?? spec.currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/** Solo el simbolo/codigo de la moneda, para encabezar una columna. */
export function currencySymbol(currency?: string, spec: LocaleSpec = activeSpec) {
  const code = currency ?? spec.currency
  try {
    const parts = numberFormat(spec.locale, {
      style: "currency",
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0)
    return parts.find((part) => part.type === "currency")?.value ?? code
  } catch {
    return code
  }
}

export function formatPercent(
  value: number | null | undefined,
  fractionDigits = 1,
  options?: { spec?: LocaleSpec },
) {
  if (isBlank(value)) return ""
  const spec = options?.spec ?? activeSpec
  return numberFormat(spec.locale, {
    style: "percent",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)
}

/** Entero con separador de miles local: dias, conteos, unidades. */
export function formatInteger(value: number | null | undefined, options?: { spec?: LocaleSpec }) {
  if (isBlank(value)) return ""
  const spec = options?.spec ?? activeSpec
  return numberFormat(spec.locale, { maximumFractionDigits: 0 }).format(value)
}

/**
 * ISO (yyyy-mm-dd) -> formato local, sin depender de la zona horaria del
 * navegador: una fecha de emision no puede correrse un dia por estar en UTC-5.
 */
export function formatDate(iso: string | null | undefined, options?: { spec?: LocaleSpec }) {
  if (!iso) return ""
  const [datePart] = iso.split("T")
  const [y, m, d] = datePart.split("-")
  if (!y || !m || !d) return iso
  const spec = options?.spec ?? activeSpec
  const utc = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)))
  return dateTimeFormat(spec.locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(utc)
}

/**
 * ISO -> fecha con el mes por su NOMBRE: «1 jul 2026».
 *
 * Existe por una ambiguedad que cuesta dinero. `<input type="date">` se pinta
 * con el formato del NAVEGADOR, no con el de la aplicacion: el mismo campo
 * muestra «01/07/2026» en un navegador en español y «07/01/2026» en uno en
 * ingles. Las dos cadenas son fechas VALIDAS y distintas —1 de julio y 7 de
 * enero— asi que no hay nada raro que mirar: el estado contable sale de otro
 * rango y cuadra igual.
 *
 * Es el mismo problema que `SelectorDePeriodo` resolvio para el mes («August
 * 2026» sobre una pantalla en español), en su version silenciosa. Un mes con
 * NOMBRE no se puede leer al reves en ninguna configuracion, y por eso el eco
 * que acompaña a cada campo de fecha usa esto y no `formatDate`.
 */
export function formatDateLegible(
  iso: string | null | undefined,
  options?: { spec?: LocaleSpec },
) {
  if (!iso) return ""
  const [datePart] = iso.split("T")
  const [y, m, d] = datePart.split("-")
  if (!y || !m || !d) return iso
  const spec = options?.spec ?? activeSpec
  const utc = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)))
  if (Number.isNaN(utc.getTime())) return iso
  /*
    `localeTexto` AQUÍ SÍ, y en `formatDate` NO. La diferencia es el mes: aquí va
    escrito («4 jul 2026») y allí va en cifras («04/07/2026»). Donde el mes es
    texto no hay orden de día y mes que confundir, así que puede ir en el idioma
    de quien lee; donde van dos números seguidos, el orden lo decide el país y
    cambiarlo convierte el 4 de julio en el 7 de abril.
  */
  return dateTimeFormat(spec.localeTexto ?? spec.locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(utc)
}

export function formatDateTime(iso: string | null | undefined, options?: { spec?: LocaleSpec }) {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  const spec = options?.spec ?? activeSpec
  return dateTimeFormat(spec.locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

/** "2026-07" -> "julio 2026" / "julho 2026" / "July 2026" */
export function formatPeriod(period: string | null | undefined, options?: { spec?: LocaleSpec }) {
  if (!period) return ""
  const [y, m] = period.split("-")
  const month = Number(m)
  if (!y || Number.isNaN(month) || month < 1 || month > 12) return period
  const spec = options?.spec ?? activeSpec
  const utc = new Date(Date.UTC(Number(y), month - 1, 1))
  // `localeTexto` y no `locale`: el mes va en el idioma de quien lee. Ver LocaleSpec.
  return dateTimeFormat(spec.localeTexto ?? spec.locale,
                        { month: "long", year: "numeric", timeZone: "UTC" }).format(utc)
}

/** Version corta del periodo para columnas estrechas: "jul 2026". */
export function formatPeriodShort(period: string | null | undefined, options?: { spec?: LocaleSpec }) {
  if (!period) return ""
  const [y, m] = period.split("-")
  const month = Number(m)
  if (!y || Number.isNaN(month) || month < 1 || month > 12) return period
  const spec = options?.spec ?? activeSpec
  const utc = new Date(Date.UTC(Number(y), month - 1, 1))
  return dateTimeFormat(spec.localeTexto ?? spec.locale,
                        { month: "short", year: "numeric", timeZone: "UTC" }).format(utc)
}

/** Dias entre hoy y una fecha ISO. Negativo = vencido. */
export function daysUntil(iso: string) {
  const target = new Date(`${iso.split("T")[0]}T00:00:00`)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((target.getTime() - today.getTime()) / 86400000)
}

/** Variacion con signo explicito: en un diff el signo es la informacion. */
export function formatDelta(value: number, options?: { spec?: LocaleSpec }) {
  const sign = value > 0 ? "+" : ""
  return `${sign}${formatAmount(value, options)}`
}
